/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-call */

import {
  Buffer,
  createCipheriv,
  createDecipheriv,
  createECDH,
} from 'browser-crypto';
import CryptoJS from 'crypto-js';
import EVP_BytesToKey from 'evp_bytestokey';

import { SECRET_KEY } from '@configs';

export class Encryption {
  private static readonly generateRandomHashArray = (
    length: number = 32
  ): CryptoJS.lib.WordArray => {
    return CryptoJS.lib.WordArray.random(length);
  };

  static generateHash(input: string): string {
    return CryptoJS.SHA256(input).toString(CryptoJS.enc.Hex);
  }

  static encrypt(
    objToEncrypt: object | string,
    sharedKey: string = SECRET_KEY
  ): string {
    const IV = Buffer.alloc(16);
    const cipher = createCipheriv(
      'aes-256-gcm',
      Buffer.from(sharedKey, 'hex'),
      IV
    );
    let encrypted = cipher.update(
      JSON.stringify(objToEncrypt),
      'utf8',
      'hex'
    ) as string;
    encrypted += cipher.final('hex');
    const authTag = cipher.getAuthTag().toString('hex') as string;
    const payload = IV.toString('hex') + encrypted + authTag;
    const payload64 = Buffer.from(payload, 'hex').toString('base64') as string;
    return payload64;
  }

  static decrypt<T>(
    objToDecrypt: string,
    sharedKey: string = SECRET_KEY
  ): T | null {
    try {
      const payload = Buffer.from(objToDecrypt, 'base64').toString('hex');
      const IV = payload.substr(0, 32);
      const encrypted = payload.substr(32, payload.length - 32 - 32);
      const authTag = payload.substr(payload.length - 32, 32);

      const decipher = createDecipheriv(
        'aes-256-gcm',
        Buffer.from(sharedKey, 'hex'),
        Buffer.from(IV, 'hex')
      );

      decipher.setAuthTag(Buffer.from(authTag, 'hex'));
      let decrypted: string = decipher.update(encrypted, 'hex', 'utf8');
      decrypted += decipher.final('utf8');

      return decrypted as T;
    } catch (error) {
      console.error('Failed to decrypt data: ', error);
      return null;
    }
  }

  static decryptAndParseObject<T>(
    objToDecrypt: string,
    sharedKey?: string
  ): T | null {
    return JSON.parse(this.decrypt(objToDecrypt, sharedKey) as string);
  }

  static createPairKeyForPatient = () => {
    const patientKeys = createECDH('secp256k1');
    patientKeys.generateKeys();

    return {
      publicKey: patientKeys.getPublicKey().toString('base64'),
      privateKey: patientKeys.getPrivateKey().toString('base64'),
      patientKeys,
    };
  };

  static getSharedSecretKey(patientKeys: any, publicKey: string): string {
    return patientKeys.computeSecret(publicKey, 'base64', 'hex');
  }

  static createSecretAnswerHash = (email: string, answer: string) => {
    const evenEmailCharacters: string[] = [];
    const oddEmailCharacters: string[] = [];
    let secret = '';

    for (let i = 0; i < email.length; i += 1)
      (i % 2 === 0 ? evenEmailCharacters : oddEmailCharacters).push(email[i]);

    const emailToAppend = (
      email.length % 2 === 0 ? evenEmailCharacters : oddEmailCharacters
    ).join('');

    secret = `${answer}${emailToAppend}`;
    return this.generateHash(secret);
  };

  static encryptBySecret(input: string, secret: string): string {
    const key = CryptoJS.enc.Hex.parse(secret);
    const iv = this.generateRandomHashArray(16);

    return CryptoJS.AES.encrypt(input, key, { iv }).toString();
  }

  static decryptBySecret(input: string, secret: string): string {
    const key = CryptoJS.enc.Hex.parse(secret);
    const iv = this.generateRandomHashArray(16);

    return CryptoJS.AES.decrypt(input, key, { iv }).toString(CryptoJS.enc.Utf8);
  }

  static decryptLegacyAES128 = (input: string, pin: string): string => {
    const result = EVP_BytesToKey(pin, null, 128, 16);
    const decipher = createDecipheriv('aes128', result.key, result.iv);
    const decrypted =
      decipher.update(input, 'hex', 'utf8') + decipher.final('utf8');
    return decrypted.toString();
  };

  public static readonly generateRandomHashString = (
    length: number = 32
  ): string => this.generateRandomHashArray(length).toString(CryptoJS.enc.Hex);
}
